/**
 * Contact Us Section — Contact Form 7 UX helpers and Select2 initialisation.
 */
// eslint-disable-next-line import/no-unresolved -- jQuery is externalised by webpack.
import jQuery from 'jquery';
import 'select2';
import 'select2/dist/css/select2.min.css';

const BLOCK_SELECTOR = '[data-alteia-contact-us-form]';
const SELECT2_CLASS = 'alteia-contact-us-select2';
const SUBMIT_NORMALIZED_ATTR = 'data-alteia-submit-normalized';

/**
 * Whether the contact submit button already contains label + arrow internally.
 *
 * @param {HTMLButtonElement} button Submit button.
 * @return {boolean}
 */
function submitButtonIsComplete( button ) {
	return Boolean(
		button.querySelector( ':scope > span:not(.alteia-cta-button__arrow)' ) &&
			button.querySelector( ':scope > .alteia-cta-button__arrow' )
	);
}

/**
 * Move orphaned label/arrow siblings into the CF7 submit button.
 *
 * Live/cached HTML can render an empty `<button>` with "Send message" and the
 * CTA arrow as sibling spans (PHP enhancement ran before content was merged).
 * Idempotent: safe to run after initial load and after CF7 DOM events.
 *
 * @param {HTMLElement} wrap Contact form wrapper.
 */
function normalizeContactSubmitButtons( wrap ) {
	const rows = wrap.querySelectorAll(
		'.contact-form__submit > p, .wpcf7-form > p:has(> .wpcf7-submit)'
	);

	rows.forEach( ( row ) => {
		const button = row.querySelector(
			':scope > button.wpcf7-submit, :scope > button.alteia-contact-us-form__submit'
		);

		if ( ! ( button instanceof HTMLButtonElement ) ) {
			return;
		}

		if (
			button.getAttribute( SUBMIT_NORMALIZED_ATTR ) === '1' &&
			submitButtonIsComplete( button )
		) {
			return;
		}

		const orphanLabel = [ ...row.children ].find(
			( node ) =>
				node instanceof HTMLSpanElement &&
				! node.classList.contains( 'wpcf7-spinner' ) &&
				! node.classList.contains( 'alteia-cta-button__arrow' ) &&
				Boolean( node.textContent?.trim() )
		);

		const orphanArrow = row.querySelector(
			':scope > .alteia-cta-button__arrow'
		);

		if ( ! button.querySelector( ':scope > span:not(.alteia-cta-button__arrow)' ) ) {
			const labelText =
				orphanLabel?.textContent?.trim() ||
				button.textContent?.trim() ||
				'Send message';

			button.replaceChildren();
			const labelSpan = document.createElement( 'span' );
			labelSpan.textContent = labelText;
			button.appendChild( labelSpan );
		}

		if (
			orphanArrow instanceof HTMLElement &&
			! button.querySelector( ':scope > .alteia-cta-button__arrow' )
		) {
			button.appendChild( orphanArrow );
		}

		orphanLabel?.remove();

		button.classList.add(
			'alteia-contact-us-form__submit',
			'alteia-cta-button',
			'has-icon'
		);
		button.setAttribute( SUBMIT_NORMALIZED_ATTR, '1' );
	} );
}

/**
 * Whether the user prefers reduced motion.
 *
 * @return {boolean}
 */
function prefersReducedMotion() {
	return (
		typeof window !== 'undefined' &&
		window.matchMedia &&
		window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
	);
}

/**
 * Resolve or create the nearest `.dropdown-wrapper` for a select field.
 *
 * Prefers an existing wrapper from CF7 form markup. When missing, wraps the
 * field's `.wpcf7-form-control-wrap` (or the select itself) so Select2 can
 * append its dropdown inside a scoped parent instead of the form/body.
 *
 * @param {Object} $select jQuery select element.
 * @return {Object} Dropdown parent wrapper.
 */
function resolveDropdownParent( $select ) {
	const $wrapper = $select.closest( '.dropdown-wrapper' );

	if ( $wrapper.length ) {
		return $wrapper;
	}

	const $controlWrap = $select.closest( '.wpcf7-form-control-wrap' );
	const $target = $controlWrap.length ? $controlWrap : $select;

	$target.wrap( '<div class="dropdown-wrapper"></div>' );

	return $select.closest( '.dropdown-wrapper' );
}

/**
 * Destroy Select2 instances inside a wrapper.
 *
 * @param {HTMLElement} wrap Form wrapper.
 */
function destroySelect2( wrap ) {
	const $ = jQuery;
	$( wrap )
		.find( `select.${ SELECT2_CLASS }` )
		.each( function destroyInstance() {
			const $select = $( this );
			if ( $select.hasClass( 'select2-hidden-accessible' ) ) {
				$select.select2( 'destroy' );
			}
			$select.removeClass( SELECT2_CLASS );
		} );
}

/**
 * Initialise Select2 for select fields inside a contact form wrapper.
 *
 * @param {HTMLElement} wrap Form wrapper.
 */
function initSelect2( wrap ) {
	if ( typeof jQuery.fn.select2 !== 'function' ) {
		return;
	}

	const $ = jQuery;

	$( wrap )
		.find( 'select' )
		.each( function initInstance() {
			const select = this;

			if ( ! ( select instanceof HTMLSelectElement ) ) {
				return;
			}

			const $select = $( select );

			// Skip already-initialised fields to avoid duplicate containers.
			if ( $select.hasClass( 'select2-hidden-accessible' ) ) {
				return;
			}

			const $dropdownParent = resolveDropdownParent( $select );

			if ( ! $dropdownParent.length ) {
				return;
			}

			$select.addClass( SELECT2_CLASS );

			$select.select2( {
				width: '100%',
				minimumResultsForSearch: -1,
				dropdownParent: $dropdownParent,
				dropdownAutoWidth: false,
				dropdownCssClass: 'alteia-contact-us-select2-dropdown',
			} );
		} );
}

/**
 * Mark a contact form wrapper as submitting / idle.
 *
 * @param {HTMLElement} wrap Form wrapper.
 * @param {boolean}     busy Whether submission is in progress.
 */
function setBusy( wrap, busy ) {
	wrap.classList.toggle( 'is-submitting', busy );
	wrap.querySelectorAll(
		'.wpcf7-submit, .alteia-contact-us-form__submit'
	).forEach( ( button ) => {
		if (
			button instanceof HTMLButtonElement ||
			button instanceof HTMLInputElement
		) {
			button.disabled = busy;
			button.setAttribute( 'aria-busy', busy ? 'true' : 'false' );
		}
	} );
}

/**
 * Bind CF7 DOM events for a single contact form wrapper.
 *
 * @param {HTMLElement} wrap Wrapper with [data-alteia-contact-us-form].
 */
function bindCf7Wrap( wrap ) {
	normalizeContactSubmitButtons( wrap );

	if ( wrap.dataset.alteiaContactCf7Bound === '1' ) {
		initSelect2( wrap );
		return;
	}

	wrap.dataset.alteiaContactCf7Bound = '1';

	const form = wrap.querySelector( '.wpcf7-form' );

	if ( ! form ) {
		return;
	}

	initSelect2( wrap );

	const reinit = () => {
		normalizeContactSubmitButtons( wrap );
		destroySelect2( wrap );
		initSelect2( wrap );
	};

	form.addEventListener( 'wpcf7submit', () => {
		setBusy( wrap, false );
		window.requestAnimationFrame( reinit );
	} );

	form.addEventListener( 'wpcf7invalid', () => {
		setBusy( wrap, false );
		wrap.classList.add( 'is-invalid' );
		wrap.classList.remove( 'is-sent' );
		window.requestAnimationFrame( reinit );
	} );

	form.addEventListener( 'wpcf7spam', () => {
		setBusy( wrap, false );
		wrap.classList.add( 'is-invalid' );
		wrap.classList.remove( 'is-sent' );
	} );

	form.addEventListener( 'wpcf7mailfailed', () => {
		setBusy( wrap, false );
		wrap.classList.add( 'is-invalid' );
		wrap.classList.remove( 'is-sent' );
	} );

	form.addEventListener( 'wpcf7mailsent', () => {
		setBusy( wrap, false );
		wrap.classList.remove( 'is-invalid' );
		wrap.classList.add( 'is-sent' );
		window.requestAnimationFrame( reinit );
	} );

	form.addEventListener( 'submit', () => {
		setBusy( wrap, true );
		wrap.classList.remove( 'is-invalid', 'is-sent' );
	} );

	form.querySelectorAll( 'input, textarea, select' ).forEach( ( field ) => {
		field.addEventListener( 'input', () => {
			if ( wrap.classList.contains( 'is-invalid' ) ) {
				field
					.closest( '.wpcf7-form-control-wrap' )
					?.classList.remove( 'is-field-invalid' );
			}
		} );
	} );
}

/**
 * Initialise contact form wrappers within a root node.
 *
 * @param {ParentNode} root Root node.
 */
export function initContactUsForms( root = document ) {
	root.querySelectorAll( BLOCK_SELECTOR ).forEach( ( wrap ) => {
		if ( wrap instanceof HTMLElement ) {
			bindCf7Wrap( wrap );
		}
	} );
}

/**
 * Entry point for the block view script.
 */
function boot() {
	initContactUsForms( document );

	// CF7 may insert the spinner after its own init; re-normalize once settled.
	window.requestAnimationFrame( () => {
		document.querySelectorAll( BLOCK_SELECTOR ).forEach( ( wrap ) => {
			if ( wrap instanceof HTMLElement ) {
				normalizeContactSubmitButtons( wrap );
			}
		} );
	} );

	if ( prefersReducedMotion() ) {
		document.documentElement.classList.add(
			'alteia-contact-us-reduced-motion'
		);
	}

	document.addEventListener( 'wpcf7domready', ( event ) => {
		const target = event.target;

		if ( ! ( target instanceof HTMLElement ) ) {
			return;
		}

		const wrap = target.closest( BLOCK_SELECTOR );

		if ( wrap instanceof HTMLElement ) {
			normalizeContactSubmitButtons( wrap );
		}
	} );

	document.addEventListener( 'wpcf7submit', ( event ) => {
		const target = event.target;

		if ( ! ( target instanceof HTMLElement ) ) {
			return;
		}

		const wrap = target.closest( BLOCK_SELECTOR );

		if ( wrap instanceof HTMLElement ) {
			window.requestAnimationFrame( () => {
				normalizeContactSubmitButtons( wrap );
				destroySelect2( wrap );
				initSelect2( wrap );
			} );
		}
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', boot );
} else {
	boot();
}
